import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { X, MapPin, Loader2, AlertCircle, Navigation, Sparkles } from 'lucide-react';
import { cn } from '../ui/cn';
import CopilotPanel from './CopilotPanel';
import type { UseTripCopilotState } from '../../hooks/useTripCopilot';
import type { TripSpec } from '../../types';

export type CopilotSnap = 'peek' | 'half' | 'full' | 'pill';

interface CopilotMobileSheetProps {
  copilot: UseTripCopilotState;
  onStartNavigation: () => void;
  onClose: () => void;
  /** Når et plass-kort er åpent tvinges arket til pille (D3). */
  spotCardOpen: boolean;
  routeStats?: { km: number; durationText: string } | null;
  onExploreStop?: (spot: import('../../types').CampingSpot) => void;
  onExploreAttraction?: (a: import('../../services/tripPlannerService').RouteAttraction) => void;
  /** Meld fra når chatten er utvidet (full/half) → App skjuler RoutePlanBar for å unngå
   *  to bunn-UI-er. Når pille/peek vises RoutePlanBar som for alle andre ruter. */
  onExpandedChange?: (expanded: boolean) => void;
  /** Inkrementer for å be arket utvide seg til full (f.eks. «Utforsk ruten» i RoutePlanBar). */
  expandSignal?: number;
}

const SNAP_DVH: Record<Exclude<CopilotSnap, 'pill'>, number> = { peek: 14, half: 52, full: 90 };

function routeSummary(spec: TripSpec, t: TFunction): string {
  const from = (spec.resolvedOriginName || spec.origin || '').split(',')[0].trim();
  const to = (spec.resolvedDestName || spec.destination || '').split(',')[0].trim();
  return from && to ? `${from} → ${to}` : (to ? t('copilot.mobile.trip_to', { place: to }) : t('copilot.title'));
}

/**
 * Mobil dra-ark for reise-copiloten (D-beslutninger fra plan-design-review).
 * 3 snap-punkt + pille. Kartet forblir synlig/interaktivt bak i peek. På desktop
 * brukes IKKE denne — der er copiloten et sidepanel (SidebarPanel).
 *
 *   peek  → tynn stripe: rute-sammendrag + status + [Juster][▶] (kart synlig bak)
 *   half  → sammendrag + chat (kart halvt synlig)
 *   full  → full chat (kart dekket)
 *   pill  → «↑ Copilot» bunn-HØYRE (når plass-kort åpent)
 */
const CopilotMobileSheet: React.FC<CopilotMobileSheetProps> = ({ copilot, onStartNavigation, onClose, spotCardOpen, routeStats, onExploreStop, onExploreAttraction, onExpandedChange, expandSignal }) => {
  const { t } = useTranslation();
  const { phase, tripSpec, planResult, planning, thinking, error } = copilot;
  const [snap, setSnap] = useState<CopilotSnap>('full');
  const [dragDvh, setDragDvh] = useState<number | null>(null); // live høyde under dra
  const [pillHint, setPillHint] = useState(false); // H4: engangs-veiviser når arket kollapser
  const dragStart = useRef<{ y: number; dvh: number } | null>(null);

  // Fase → fornuftig standard-snap. Bruker-dra overstyrer (vi rører ikke snap etter dra
  // med mindre fasen endrer seg til planned første gang).
  const prevPhase = useRef(phase);
  useEffect(() => {
    if (phase !== prevPhase.current) {
      // Rute tegnet → minimer copiloten til pille, så STANDARD RoutePlanBar tar over
      // (ruten «vises som alle andre ruter»: alternativer/Vis ruteinnhold/Lagre/nav).
      if (phase === 'planned') setSnap('pill');
      else setSnap('full');                            // intervju/forslag = chat
      prevPhase.current = phase;
    }
  }, [phase]);

  // Meld utvidet-tilstand (full/half) til App så RoutePlanBar kan skjules mens man chatter.
  useEffect(() => { onExpandedChange?.(snap === 'full' || snap === 'half'); }, [snap, onExpandedChange]);

  // Ekstern «utforsk»-forespørsel (RoutePlanBar «Utforsk ruten») → utvid til full.
  const prevExpandSignal = useRef(expandSignal);
  useEffect(() => {
    if (expandSignal !== undefined && expandSignal !== prevExpandSignal.current) {
      prevExpandSignal.current = expandSignal;
      setSnap('full');
    }
  }, [expandSignal]);

  // Rute tegnet/re-tegnet → minimer chatten så KARTET kommer frem. Fase-effekten over
  // dekker kun proposal→planned FØRSTE gang; justeringer i planned-fase (re-plan) lot
  // chatten bli stående i full og dekke kartet. Trigger: planning true→false m/ resultat.
  const prevPlanning = useRef(planning);
  useEffect(() => {
    if (prevPlanning.current && !planning && planResult) {
      setSnap(phase === 'planned' ? 'pill' : 'peek');
    }
    prevPlanning.current = planning;
  }, [planning, planResult, phase]);

  // Plass-kort åpent → pille; lukkes → tilbake til forrige snap. Reverterer KUN når VI
  // tvang pille pga. kortet (ellers ville en allerede-minimert copilot i planned uventet
  // sprette til peek når kortet lukkes).
  const beforePillSnap = useRef<CopilotSnap>('peek');
  const pillForcedBySpotCard = useRef(false);
  useEffect(() => {
    if (spotCardOpen && snap !== 'pill') {
      beforePillSnap.current = snap;
      pillForcedBySpotCard.current = true;
      setSnap('pill');
    } else if (!spotCardOpen && pillForcedBySpotCard.current) {
      pillForcedBySpotCard.current = false;
      setSnap(beforePillSnap.current);
    }
  }, [spotCardOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Minimert tilstand: når ruten ER tegnet lar vi den STANDARD RoutePlanBar eie bunnen,
  // og copiloten krymper til pille — ellers dupliserer kikk-stripa RoutePlanBars sammendrag
  // (to bunn-UI-er oppå hverandre). Uten tegnet rute er kikk-stripa eneste sammendrag.
  const minimizedSnap: CopilotSnap = phase === 'planned' ? 'pill' : 'peek';

  // H4: første gang arket kollapser til pille etter en planlagt tur, vis engangs-hintet
  // om hvor chatten ble av. Persistert per enhet så det aldri maser to ganger. Auto-lukker.
  const PILL_HINT_KEY = 'fricamp_copilot_pill_hint_seen';
  useEffect(() => {
    if (snap !== 'pill' || phase !== 'planned') return;
    try {
      if (!localStorage.getItem(PILL_HINT_KEY)) {
        setPillHint(true);
        localStorage.setItem(PILL_HINT_KEY, '1');
      }
    } catch { /* WKWebView/kvote — bevisst stille */ }
  }, [snap, phase]);
  useEffect(() => {
    if (!pillHint) return;
    const id = setTimeout(() => setPillHint(false), 5000);
    return () => clearTimeout(id);
  }, [pillHint]);

  // Esc → minimer (eller lukk hvis allerede minimert).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (snap === 'full' || snap === 'half') setSnap(minimizedSnap);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [snap, onClose, minimizedSnap]);

  const onHandlePointerDown = useCallback((e: React.PointerEvent) => {
    dragStart.current = { y: e.clientY, dvh: SNAP_DVH[(snap === 'pill' ? 'peek' : snap)] };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  }, [snap]);

  const onHandlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragStart.current) return;
    const dyDvh = ((dragStart.current.y - e.clientY) / window.innerHeight) * 100;
    setDragDvh(Math.max(8, Math.min(94, dragStart.current.dvh + dyDvh)));
  }, []);

  const onHandlePointerUp = useCallback(() => {
    if (dragDvh == null) { dragStart.current = null; return; }
    // Snap-terskler: >65 → full, >33 → half, ellers minimert (pille når rute tegnet).
    const next: CopilotSnap = dragDvh > 65 ? 'full' : dragDvh > 33 ? 'half' : minimizedSnap;
    setSnap(next);
    setDragDvh(null);
    dragStart.current = null;
  }, [dragDvh, minimizedSnap]);

  // pointercancel (tap bort, OS-gesture, lost capture) uten opprydding lot dragDvh
  // henge → arket ble stående på mellomhøyde og Minimer/tap hjalp ikke.
  const onHandlePointerCancel = useCallback(() => {
    setDragDvh(null);
    dragStart.current = null;
  }, []);

  const toggleHandleTap = () => {
    // Rydd eventuell hengende drag-høyde før snap-toggle (ISSUE-003).
    setDragDvh(null);
    dragStart.current = null;
    setSnap((s) => (s === 'full' ? minimizedSnap : 'full'));
  };

  // ── Pille = usynlig ──
  // Den flytende «Juster»-pillen er fjernet: den ble hengende malplassert oppå kort/
  // rutefelt/kart (og meningsløs når ruten var lukket). Veiene tilbake til chatten er
  // COPILOT-fanen i bunnmenyen (expandSignal) og «Utforsk ruten» i rutefeltet.
  //
  // H4 (design-review): når arket kollapser til pille etter planlegging «forsvinner»
  // chatten for en ny bruker uten spor. Vis derfor ÉN gang (per enhet) et kort,
  // selvlukkende hint om hvor chatten ble av. Ikke en permanent knapp — bare veiviseren.
  if (snap === 'pill') {
    if (!pillHint) return null;
    return (
      <div
        className="sm:hidden fixed inset-x-0 z-drawer flex justify-center px-4"
        style={{ bottom: 'calc(4.75rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <button
          type="button" onClick={() => setPillHint(false)}
          className="flex items-center gap-2 rounded-pill border border-[var(--border-primary)] bg-[var(--bg-surface)] px-4 py-2.5 text-sm text-[var(--text-secondary)] shadow-2xl animate-in fade-in slide-in-from-bottom-2"
        >
          <Sparkles className="h-4 w-4 shrink-0 text-brand" />
          <span>{t('copilot.mobile.pill_hint')}</span>
          <X className="h-4 w-4 shrink-0 text-[var(--text-muted)]" />
        </button>
      </div>
    );
  }

  const heightDvh = dragDvh ?? SNAP_DVH[snap];
  const isPeek = snap === 'peek' && dragDvh == null;

  return (
    <div
      role="dialog"
      aria-label={t('copilot.title')}
      className="sm:hidden fixed inset-x-0 bottom-0 z-drawer flex flex-col rounded-t-panel border-t border-[var(--border-primary)] bg-[var(--bg-surface)] shadow-2xl"
      style={{ height: `${heightDvh}dvh`, transition: dragDvh == null ? 'height 200ms ease' : 'none' }}
    >
      {/* Dra-handle (også knapp: tapp = veksle full/kikk) */}
      <div
        onPointerDown={onHandlePointerDown}
        onPointerMove={onHandlePointerMove}
        onPointerUp={onHandlePointerUp}
        onPointerCancel={onHandlePointerCancel}
        className="flex shrink-0 touch-none flex-col items-center pt-2 pb-1"
      >
        <button type="button" onClick={toggleHandleTap} aria-label={isPeek ? t('copilot.mobile.open_chat_aria') : t('copilot.mobile.minimize_aria')} className="p-1">
          <span className="block h-1 w-10 rounded-full bg-[var(--border-secondary)]" />
        </button>
      </div>

      {isPeek ? (
        // ── KIKK: rute-sammendrag + status (D2: status i stripa, ikke rykk) ──
        <div className="flex items-center justify-between gap-3 px-4 pb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-sm text-[var(--text-primary)]">
              <MapPin className="h-4 w-4 shrink-0 text-brand" />
              <span className="truncate">{routeSummary(tripSpec, t)}</span>
            </div>
            {planning || thinking ? (
              <p className="mt-0.5 flex items-center gap-1 text-xs text-[var(--text-muted)]">
                <Loader2 className="h-3 w-3 animate-spin" /> {t('copilot.status.planning')}
              </p>
            ) : error ? (
              <button type="button" onClick={() => setSnap('half')} className="mt-0.5 flex items-center gap-1 text-xs text-red-400">
                <AlertCircle className="h-3 w-3" /> {t('copilot.mobile.plan_failed_tap')}
              </button>
            ) : planResult ? (
              <p className="mt-0.5 text-xs text-[var(--text-muted)]">
                {routeStats ? `${routeStats.km} km · ${routeStats.durationText} · ` : ''}
                {t('copilot.mobile.summary_stats', { days: planResult.dayPlan.length, stops: planResult.stops.length })}
                {planResult.emptyCorridor ? t('copilot.mobile.direct_route_suffix') : ''}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" onClick={() => setSnap('full')} className="min-h-[44px] rounded-pill border border-[var(--border-primary)] px-4 text-sm font-medium text-[var(--text-secondary)]">
              {planResult ? t('copilot.mobile.adjust') : t('copilot.mobile.chat')}
            </button>
            {planResult && (
              <button type="button" onClick={onStartNavigation} aria-label={t('copilot.actions.navigate')} className="grid h-11 w-11 place-items-center rounded-full bg-brand text-stone-900">
                <Navigation className="h-5 w-5" />
              </button>
            )}
            <button type="button" onClick={onClose} aria-label={t('copilot.mobile.close_aria')} className="grid h-11 w-11 place-items-center text-[var(--text-muted)]">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      ) : (
        // ── HALV/FULL: full chat-panel ──
        <div className="min-h-0 flex-1">
          <CopilotPanel
            copilot={copilot}
            onStartNavigation={onStartNavigation}
            onShowOnMap={() => setSnap(minimizedSnap)}
            routeStats={routeStats}
            onExploreStop={onExploreStop}
            onExploreAttraction={onExploreAttraction}
          />
        </div>
      )}
    </div>
  );
};

export default CopilotMobileSheet;
